import { useState } from "react";
import { cn } from "../../lib/utils";

const LOGO_DEV_PUBLIC_KEY = "pk_Zm-m87WGSp6dOBqWyVKQwg";

interface CompanyLogoProps {
  domain: string;
  name: string;
  className?: string;
  fallbackClassName?: string;
}

export const CompanyLogo = ({ 
  domain, 
  name, 
  className = "w-8 h-8",
  fallbackClassName = "bg-gradient-to-br from-primary/80 to-accent/80"
}: CompanyLogoProps) => {
  const [hasError, setHasError] = useState(false);
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  
  if (hasError) {
    return (
      <div className={cn(
        className, 
        "rounded-lg flex items-center justify-center text-xs font-semibold text-white flex-shrink-0",
        fallbackClassName
      )}>
        {initials}
      </div>
    );
  }
  
  return (
    <img 
      src={`https://img.logo.dev/${domain}?token=${LOGO_DEV_PUBLIC_KEY}`}
      alt={name}
      className={cn(className, "rounded-lg object-contain bg-white border border-border flex-shrink-0")}
      onError={() => setHasError(true)}
    />
  );
};

// Helper to extract domain from company name or URL
export const getDomainFromCompany = (company: string): string => {
  // Common SaaS company domain mappings
  const domainMap: Record<string, string> = {
    "Slack": "slack.com",
    "Notion": "notion.so",
    "Figma": "figma.com",
    "Linear": "linear.app",
    "Vercel": "vercel.com",
    "Stripe": "stripe.com",
    "Intercom": "intercom.com",
    "HubSpot": "hubspot.com",
    "Airtable": "airtable.com",
    "Webflow": "webflow.com",
    "Amplitude": "amplitude.com",
    "Segment": "segment.com",
    "Mixpanel": "mixpanel.com",
    "Loom": "loom.com",
    "Miro": "miro.com",
    "Datadog": "datadoghq.com",
    "GitHub": "github.com",
    "Salesforce": "salesforce.com",
    "Gmail": "gmail.com",
    "Google Calendar": "calendar.google.com",
    "Zendesk": "zendesk.com",
    "Zapier": "zapier.com",
    "Google Sheets": "sheets.google.com",
    "Marketo": "marketo.com",
    "Clearbit": "clearbit.com",
    "Drift": "drift.com",
    "Mailchimp": "mailchimp.com",
    "Twilio": "twilio.com",
    "SendGrid": "sendgrid.com",
  };
  
  return domainMap[company] || `${company.toLowerCase().replace(/\s+/g, '')}.com`;
};
